import { Device, User } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import useTheme from "@mui/material/styles/useTheme";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { endOfDay } from "date-fns/endOfDay";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SettingsPopup from "./components/filter/SettingsPopup";
import LogbookCheckHelp from "./components/LogbookCheckHelp";
import { FilterStackLayout } from "@/components/layout/FilterStackLayout";
import { DEFAULT_DISTANCE_DECIMALS, toDistanceDecimals } from "@/constants/distanceDecimals";
import { useFilteredTrips } from "@/hooks/trips/useFilteredTrips";
import { useTripsByDateRange } from "@/hooks/trips/useTripsByDateRange";
import { usePersistedDateRange } from "@/hooks/userState/usePersistedDateRange";
import { useUserState } from "@/hooks/userState/useUserState";
import { TripDetailsErrorLines } from "@/pages/logbook-edit/components/TripDetailsErrorLines";
import { demystifyError } from "@/utilities/error/demystifyError";
import { DeviceCapError } from "@/utilities/error/DeviceCapError";
import { mapLazy } from "@/utilities/mapLazy";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";

const TripsGrid = mapLazy("TripsGrid", import("./components/TripsGrid"));
const ReasonInput = mapLazy("ReasonInput", import("@/pages/logbook-edit/components/filter/ReasonInput"));
const DateRangeSelect = mapLazy("DateRangeSelect", import("@/components/input/DateRangeSelect"));
const DriverOrDeviceSelect = mapLazy(
  "DriverOrDeviceSelect",
  import("@/components/input/DriverOrDeviceSelect"),
);

export default function LogbookCheckPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [reasons, setReasons] = useState<string[]>([]);
  const [vehicleStatus, setVehicleStatus] = useState(false);
  const { fromDate, toDate, setRange, isReady } = usePersistedDateRange({
    key: "dateRange:logbookCheck",
    defaultLookbackDays: 1,
  });
  const { value: settings, setValue: setSettings } = useUserState("logbookCheck:settings", {
    distanceDecimals: DEFAULT_DISTANCE_DECIMALS as number,
  });
  const distanceDecimals = toDistanceDecimals(settings.distanceDecimals);
  const { t } = useTranslation();

  const {
    data: trips,
    detailsPending,
    detailsError,
    detailsErrorSource,
    annotationsError,
    annotationProgress,
    error: tripsError,
  } = useFilteredTrips({
    users,
    devices,
    toDate,
    fromDate,
    reasons,
    enabled: isReady,
  });
  // Same query key as inside useFilteredTrips (deduplicated by TanStack Query); the
  // unfiltered list keeps "previous trip" lookups correct when a reason filter is active.
  const { data: allTrips } = useTripsByDateRange({
    users,
    devices,
    toDate,
    fromDate,
    enabled: isReady,
  });
  const annotationsLoading =
    !!annotationProgress && annotationProgress.totalCount > 0 && annotationProgress.read < annotationProgress.totalCount;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(600));

  const report = useMemo(
    () => ({
      title: t("logbookCheck.title"),
      period: { from: fromDate, to: toDate },
      filter: [
        ...users.map(user => getUserDisplayName(user)),
        ...devices.map(device => device.name),
        ...reasons,
      ].join(", "),
    }),
    [t, fromDate, toDate, users, devices, reasons],
  );

  return (
    <FilterStackLayout
      filterMaxWidth={1512}
      isMobile={isMobile}
      filters={
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <SettingsPopup
              vehicleStatus={vehicleStatus}
              setVehicleStatus={setVehicleStatus}
              distanceDecimals={distanceDecimals}
              setDistanceDecimals={decimals => setSettings({ distanceDecimals: decimals })}
            />
            <LogbookCheckHelp />
            <DriverOrDeviceSelect
              users={users}
              devices={devices}
              onChange={(nextUsers?: User[], nextDevices?: Device[]) => {
                setUsers(nextUsers ?? []);
                setDevices(nextDevices ?? []);
              }}
              onlyDrivers={false}
              showArchived={false}
              showArchivedVehicles={vehicleStatus}
            />
          </Box>
          <ReasonInput onChange={setReasons} />
          <DateRangeSelect
            toDate={toDate}
            fromDate={fromDate}
            onChange={(from, to) => {
              setRange(from, to ?? (from ? endOfDay(from) : undefined));
            }}
          />
        </Box>
      }
      content={
        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0 }}>
          {(tripsError || detailsError) && (
            <Alert severity="error">
              {tripsError instanceof DeviceCapError
                ? demystifyError(t, tripsError)
                : t("logbookCheck.loadError")}
              <TripDetailsErrorLines source={detailsErrorSource} annotationsError={annotationsError} />
            </Alert>
          )}
          {annotationsLoading && (
            <Box>
              <LinearProgress
                variant="determinate"
                value={(annotationProgress.read / annotationProgress.totalCount) * 100}
              />
              <Typography variant="caption" color="text.secondary">
                {t("logbookCheck.annotationProgress", {
                  read: annotationProgress.read,
                  total: annotationProgress.totalCount,
                })}
              </Typography>
            </Box>
          )}
          <TripsGrid
            trips={trips}
            addressTrips={allTrips}
            distanceDecimals={distanceDecimals}
            // No spinner next to an error alert: without trips or annotations nothing more arrives.
            loading={!trips && !tripsError && !detailsError}
            detailsFailed={!!detailsError}
            tripsFailed={!!tripsError || !!detailsError}
            report={report}
            exportDisabled={!trips || trips.length === 0 || detailsPending || !!detailsError}
          />
        </Box>
      }
    />
  );
}
